import { Divider, Illustration, Text, Title } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useAssessmentConfirmedNotificationIssue } from "@src/appV2/SkillsAssessment/api/useAssessmentConfirmedNotificationIssue";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { format } from "date-fns-tz";
import { useState } from "react";

interface Props {
  scheduledFor: string;
  showTimezone: boolean;
  onContinue: () => void;

  assessmentId?: string;
}

export function OrientationScheduledConfirmation(props: Props) {
  const { scheduledFor, showTimezone, onContinue, assessmentId } = props;

  const [confirmationResent, setConfirmationResent] = useState(false);

  const { mutateAsync: resendText, isLoading } = useAssessmentConfirmedNotificationIssue();

  const { showSuccessToast } = useToast();

  return (
    <Stack justifyContent="space-between" width="100%" height="100%">
      <Stack spacing={8}>
        <Title variant="h3" component="h2">
          You booked your Demo Shift for{" "}
          <span>
            {format(parseISO(scheduledFor), "MMMM do")} at{" "}
            {format(parseISO(scheduledFor), `h:mmaaa${showTimezone ? " z" : ""}`)}
          </span>
        </Title>

        <Stack py={6} alignItems="center">
          <Illustration type="shift-booked" />
        </Stack>

        <Text light>
          Come back to the app at your scheduled time to show your punctuality for shifts. We also
          sent you a text with the link to join the Demo Shift.
        </Text>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text light>Haven&apos;t received a confirmation text?</Text>
          <TextButton
            disabled={confirmationResent || isLoading}
            underline={!confirmationResent}
            onClick={async () => {
              await resendText({
                assessmentId: assessmentId ?? "",
              });
              logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_RESEND_TEXT_CLICKED, {
                assessmentId,
              });
              setConfirmationResent(true);
              showSuccessToast("Confirmation text resent! (You will receive a new text shortly)");
            }}
          >
            {confirmationResent ? "Resent!" : "Resend"}
          </TextButton>
        </Stack>

        <Divider />

        <Text bold>
          Remember: think of it like your first shift at a facility on Clipboard. We&apos;re
          counting on you to show up on time!
        </Text>
      </Stack>

      <Button
        onClick={() => {
          onContinue();
        }}
      >
        Continue
      </Button>
    </Stack>
  );
}
